import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from '../../styles/layout/Celebration.module.css';

// Animation types - exported for dev tools
export const CELEBRATION_TYPES = ['confetti', 'fireworks', 'balloons'];

/**
 * Celebration - A one-time burst celebration animation
 * Randomly selects from: confetti, fireworks, or balloons
 * 
 * @param {boolean} trigger - When this becomes true, triggers the celebration
 * @param {function} onComplete - Called when the animation finishes
 * @param {string} type - Optional specific animation type
 */
const Celebration = ({ trigger, onComplete, type = null }) => {
  const [particles, setParticles] = useState([]);
  const [animationType, setAnimationType] = useState(null);
  const hasTriggeredRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  
  // Colors for particles
  const COLORS = [
    '#FF6B6B', // coral red
    '#4ECDC4', // teal
    '#FFE66D', // yellow
    '#95E1D3', // mint
    '#F38181', // salmon
    '#AA96DA', // lavender
    '#FCBAD3', // pink
    '#A8D8EA', // light blue
    '#FF9F43', // orange
    '#6C5CE7', // purple
  ];

  const generateParticles = useCallback((type) => {
    const newParticles = [];
    
    switch (type) {
      case 'confetti': {
        // 70 confetti pieces falling from various heights
        for (let i = 0; i < 70; i++) {
          newParticles.push({
            id: i,
            type: 'confetti',
            left: Math.random() * 100, // percentage
            top: -20 - Math.random() * 30, // spread starting positions from -20 to -50
            delay: Math.random() * 0.5,
            duration: 1.5 + Math.random() * 0.5,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            rotation: Math.random() * 360,
            size: 12 + Math.random() * 9,
          });
        }
        break;
      }
      
      case 'fireworks': {
        // 3-4 firework bursts at well-distributed positions
        const burstCount = 3 + Math.floor(Math.random() * 2);
        let particleId = 0;
        const trailDuration = 0.5; // seconds for trail to reach burst point
        
        // Pre-defined zones to ensure good separation
        const zones = [
          { x: 15 + Math.random() * 15, y: 15 + Math.random() * 20 },   // top-left
          { x: 42 + Math.random() * 16, y: 25 + Math.random() * 20 },   // center
          { x: 70 + Math.random() * 15, y: 15 + Math.random() * 20 },   // top-right
          { x: 25 + Math.random() * 15, y: 45 + Math.random() * 15 },   // bottom-left
          { x: 60 + Math.random() * 15, y: 45 + Math.random() * 15 },   // bottom-right
        ];
        
        for (let burst = 0; burst < burstCount; burst++) {
          const zone = zones[burst % zones.length];
          const burstX = zone.x;
          const burstY = zone.y;
          const trailDelay = burst * 0.4; // stagger the launches
          const burstDelay = trailDelay + trailDuration; // burst happens after trail completes
          const particleCount = 16 + Math.floor(Math.random() * 8);
          
          // Add the rocket trail
          newParticles.push({
            id: particleId++,
            type: 'trail',
            left: burstX,
            targetY: burstY,
            delay: trailDelay,
            duration: trailDuration,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
          });
          
          // Add the burst particles
          for (let i = 0; i < particleCount; i++) {
            const angleRad = (i / particleCount) * 2 * Math.PI;
            const distance = 100 + Math.random() * 60;
            // Pre-calculate transform values for better browser compatibility
            const translateX = Math.cos(angleRad) * distance;
            const translateY = Math.sin(angleRad) * distance;
            newParticles.push({
              id: particleId++,
              type: 'firework',
              left: burstX,
              top: burstY,
              delay: burstDelay,
              translateX,
              translateY,
              color: COLORS[Math.floor(Math.random() * COLORS.length)],
              size: 20 + Math.random() * 15,
            });
          }
        }
        break;
      }
      
      case 'balloons': {
        // 12-16 balloons floating up
        const count = 12 + Math.floor(Math.random() * 5);
        for (let i = 0; i < count; i++) {
          newParticles.push({
            id: i,
            type: 'balloon',
            left: Math.random() * 100,
            delay: Math.random() * 0.4,
            duration: 1.8 + Math.random() * 0.4,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
            size: 20 + Math.random() * 15,
            wobble: Math.random() * 20 - 10,
          });
        }
        break;
      }
      
      default:
        break;
    }
    
    return newParticles;
  }, []);

  useEffect(() => {
    // Only trigger once per component instance.
    if (trigger && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      
      const selectedType = type && CELEBRATION_TYPES.includes(type)
        ? type
        : CELEBRATION_TYPES[Math.floor(Math.random() * CELEBRATION_TYPES.length)];
      const reducedMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reducedMotion) {
        setAnimationType(selectedType);
        setParticles(generateParticles(selectedType));
      }
      
      // Clean up after animation completes
      const timer = setTimeout(() => {
        setParticles([]);
        setAnimationType(null);
        onCompleteRef.current?.();
      }, reducedMotion ? 120 : 2800);
      
      return () => {
        clearTimeout(timer);
        // React Strict Mode replays a newly mounted animation's effect.
        // Its replacement effect must recreate the completion timer.
        hasTriggeredRef.current = false;
      };
    }
  }, [trigger, generateParticles, type]);

  if (!animationType || particles.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.celebrationContainer}
      aria-hidden="true"
      data-testid="celebration-overlay"
      data-celebration-type={animationType}
    >
      {particles.map((particle) => {
        if (particle.type === 'confetti') {
          return (
            <div
              key={particle.id}
              className={styles.confetti}
              style={{
                left: `${particle.left}%`,
                top: `${particle.top}px`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                backgroundColor: particle.color,
                transform: `rotate(${particle.rotation}deg)`,
                width: `${particle.size}px`,
                height: `${particle.size * 0.6}px`,
              }}
            />
          );
        }
        
        if (particle.type === 'trail') {
          return (
            <div
              key={particle.id}
              className={styles.trail}
              style={{
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                '--target-y': `${particle.targetY}vh`,
              }}
            />
          );
        }
        
        if (particle.type === 'firework') {
          return (
            <div
              key={particle.id}
              className={styles.firework}
              style={{
                left: `${particle.left}%`,
                top: `${particle.top}%`,
                animationDelay: `${particle.delay}s`,
                backgroundColor: particle.color,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                '--tx': `${particle.translateX}px`,
                '--ty': `${particle.translateY}px`,
              }}
            />
          );
        }
        
        if (particle.type === 'balloon') {
          return (
            <div
              key={particle.id}
              className={styles.balloon}
              style={{
                left: `${particle.left}%`,
                animationDelay: `${particle.delay}s`,
                animationDuration: `${particle.duration}s`,
                backgroundColor: particle.color,
                width: `${particle.size}px`,
                height: `${particle.size * 1.2}px`,
                '--wobble': `${particle.wobble}px`,
              }}
            />
          );
        }
        
        return null;
      })}
    </div>
  );
};

export default Celebration;
